import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faPaperclip, faStar } from '@fortawesome/free-solid-svg-icons';
import { AttributeComponent } from '@hub4b/controls/attribute-form-item/attribute.component';
import { NgxSpinnerModule } from 'ngx-spinner';
import { takeUntil } from 'rxjs/operators';
import { FolderPropertyCodes } from 'src/app/core/constantes/folder-property-codes';
import { BaseDirective } from 'src/app/core/directives/base.directive';
import { AvmAttributeFieldPipe } from 'src/app/core/pipes/avm-attribute-field.pipe';
import { DocumentType } from 'src/app/models/enums/document-type';
import { PropertyType } from 'src/app/models/enums/property-type';
import { AttributeDefinition } from 'src/app/models/server/attribute-definition';
import { Folder } from 'src/app/models/server/folder';
import { FolderDocument } from 'src/app/models/server/folder-document';
import { NotificationService } from 'src/app/services/actions/notification.service';
import { SpinnerService } from 'src/app/services/core/spinner.service';
import { FavoritesEventsService } from 'src/app/services/events/favorites-events.service';
import { DocumentsStateService } from 'src/app/services/state/documents-state.service';
import { FavoriteDocumentsStateService } from 'src/app/services/state/favorite-documents-state.service';
import { DetailBaseDatasource } from '../datasources/detail-base-datasource';

@Component({
    selector: 'app-favorites-bar',
    templateUrl: './favorites-bar.component.html',
    styleUrls: ['./favorites-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FaIconComponent, AttributeComponent, MatButtonModule, MatTooltipModule, NgxSpinnerModule, AvmAttributeFieldPipe]
})
export class FavoritesBarComponent extends BaseDirective implements OnInit {
    /**
     * Références des icônes utilisés dans le template.
     */
    public attachmentIcon = faPaperclip;
    public starIcon = faStar;

    /**
     * Références des Enums utilisées dans le template.
     */
    public PropertyType = PropertyType;

    /**
     * Références des constantes utilisées dans le template.
     */
    public WORKFLOW_ID = FolderPropertyCodes.VIEW_ID;
    public BOX_ID = FolderPropertyCodes.BOX_ID;

    /**
     * Liste des définitions des attributs affichés dans les favoris.
     */
    @Input()
    public favoriteAttributesDefinitions: AttributeDefinition[];

    /**
     * Dossier pour lequel les attributs sont affichés.
     */
    @Input()
    public folder: Folder;

    /**
     * Constructeur.
     */
    constructor(
        private favoriteStateService: FavoriteDocumentsStateService,
        private documentsStateService: DocumentsStateService,
        private favoritesEventsService: FavoritesEventsService,
        private spinner: SpinnerService,
        private notificationService: NotificationService,
        private datasource: DetailBaseDatasource,
        private cdr: ChangeDetectorRef
    ) {
        super();
    }

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit(): void {
        this.subscribeToEvents();
        this.buildFavoriteVirtualDocument();
    }

    /**
     * Appelée lorsque l'utilisateur clique su le bouton 'Afficher les fichiers favoris'.
     */
    public onDisplayFavoriteAttachments(): void {
        if (this.favoriteStateService.getFavoriteDocument) {
            if (this.favoriteStateService.getFavoriteDocument.attachments.length > 0) {
                // On sélectionne le document virtuel uniquement s'il y a des pièces-jointes
                this.documentsStateService.changeSelectedDocument(this.favoriteStateService.getFavoriteDocument);
            } else {
                this.notificationService.displayInfo('Favoris', "Aucun fichier n'est défini comme favori.");
            }
        } else {
            // Le document "virtuel" contenant les fichiers favoris est construit au 1er appel.
            this.buildFavoriteVirtualDocument();
            this.notificationService.displayInfo('Favoris', "Aucun fichier n'est défini comme favori.");
        }
    }

    /**
     * * S'abonne aux événements des autres composants.
     */
    private subscribeToEvents(): void {
        this.subscribeToRefreshRequestEvents();
        this.subscribeToFavoriteDocumentChangeEvents();
    }

    /**
     * Souscrit au événements d'ajout / suppression de document aux favoris.
     */
    private subscribeToFavoriteDocumentChangeEvents(): void {
        this.favoriteStateService.onFavoriteDocumentChange$.pipe(takeUntil(this.componentDestroyed$)).subscribe((document) => {
            if (document && this.documentsStateService.getSelectedDocument(this.folder.id) && document.id === this.documentsStateService.getSelectedDocument(this.folder.id).id) {
                // Le document selectionné correspond au document des favoris --> on affiche le document modifié

                if (this.favoriteStateService.getFavoriteDocument.attachments.length > 0) {
                    // On sélectionne le document virtuel uniquement s'il y a des pièces-jointes
                    this.documentsStateService.changeSelectedDocument(this.favoriteStateService.getFavoriteDocument);
                } else {
                    this.documentsStateService.deleteSelectedDocument(this.folder.id);
                }
            }
        });
    }

    /**
     * Souscrit aux evenement de demande de raffraichissement de la section 'Favoris'.
     */
    private subscribeToRefreshRequestEvents(): void {
        this.favoritesEventsService.needRefresh.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            if (!!event && event.folder.id == this.folder.id) {
                //console.warn(`Réception de l'événement FavoritesRefreshEvent par le composant FavoritesComponent du dossier ${this.folder.id}`, event);
                this.folder = event.folder;
                this.cdr.markForCheck();
            }
        });
    }

    /**
     * Construit le document virtuel contenant les fichiers favoris du dossier.
     */
    private buildFavoriteVirtualDocument(): void {
        this.spinner.show('favorite-spinner');
        let virtualDocument = new FolderDocument();
        virtualDocument.id = -1;
        virtualDocument.folderId = this.folder.id;
        virtualDocument.type = DocumentType.Favorite;
        virtualDocument.viewId = this.folder.viewId;

        this.retrieveFavoriteFiles(virtualDocument);
    }

    /**
     * Retrouve les fichiers favoris d'un dossier et les ajoute au document virtuel des favoris.
     * @param virtualDocument : Instance du document virtuel des favoris.
     */
    private retrieveFavoriteFiles(virtualDocument: FolderDocument): void {
        this.datasource.listDocuments(this.folder.viewId, this.folder.id).subscribe((documents) => {
            documents.forEach((document) => {
                this.addDocumentFavoriteFiles(document, virtualDocument);
            });

            this.favoriteStateService.initFavoriteDocument(virtualDocument);

            this.spinner.hide('favorite-spinner');
        });
    }

    /**
     * Ajoute les fichiers, du document spécifié, marqués comme favoris au document virtuel des favoris.
     * @param document : Instance du document original.
     * @param virtualDocument : Instance du document virtuel des favoris.
     */
    private addDocumentFavoriteFiles(document: FolderDocument, virtualDocument: FolderDocument): void {
        document.attachments.forEach((attachment) => {
            if (attachment.isFavorite) {
                virtualDocument.attachments.push(attachment);
            }
        });
    }
}
